// Вкладка «Норматив»: три числа за месяц — расчётное, ручное и действующее.
//
// Расхождение расчётного и ручного показывается ЯВНО и НЕ считается ошибкой. Это протокол
// сверки: в майской книге клиента норма стояла 540 (18 × 30), а по постам и дням выходит 558
// (18 × 31) — таких строк девять из шестидесяти. Падать на каждой значит не перенести книгу,
// прятать расхождение значит потерять его. Решение принимает человек, а система обязана
// показать оба числа и разницу между ними.
//
// Часы считает сервер и отдаёт строками. Здесь только показ и разность строк — без
// плавающей точки.

import { useEffect, useState } from 'react';

import { RiErrorWarningLine, RiScales3Line } from '@remixicon/react';
import { useNavigate } from 'react-router';

import type { SiteOut } from '@/shared/api/endpoints/org';
import type { DecimalString } from '@/shared/api/types';
import { decimalString } from '@/shared/api/types';
import { useAuthStore } from '@/shared/auth/store';
import { diffHours, formatHours, hoursWord, sameHours } from '@/shared/hours/format';
import { can } from '@/shared/rbac/permissions';
import { countLabel } from '@/shared/text/plural';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import * as Hint from '@/shared/ui/hint';
import * as Input from '@/shared/ui/input';
import * as Label from '@/shared/ui/label';
import { ScreenState, Skeleton } from '@/shared/ui/screen-state';

import { FieldList, FieldRow } from '@/shared/ui/field-row';
import { PeriodStepper } from '@/shared/ui/period-stepper';
import { useNormOverrideMutation, useNormQuery } from '@/shared/org/queries';
import { formatPeriodKey, formatPeriodTitle, parsePeriod, shiftPeriod } from '@/shared/time/period';
import type { SitesRoute } from '../route';
import { FILTER_PERIOD, setFilter, sitesHref } from '../route';

const DAY_FORMS = ['день', 'дня', 'дней'] as const;

/** Ручной норматив ставит тот, кто ведёт табель. Читать норматив может кто угодно с доступом. */
const EDIT_PERMISSION = 'core.timesheet.edit';

export function NormTab({ site, route }: { site: SiteOut; route: SitesRoute }) {
  const navigate = useNavigate();
  const permissions = useAuthStore((state) => state.permissions);
  const canEdit = can(permissions, EDIT_PERMISSION);

  const period = parsePeriod(route.search.filters[FILTER_PERIOD]);
  const query = useNormQuery(site.id, period.year, period.month);
  const mutation = useNormOverrideMutation(site.id);

  const override = query.data?.override ?? null;
  const [draft, setDraft] = useState(override ?? '');
  const [inputError, setInputError] = useState<string | null>(null);
  const [gateNote, setGateNote] = useState<string | null>(null);

  // Приехал другой месяц или сохранилось новое значение — поле догоняет данные.
  useEffect(() => {
    setDraft(override ?? '');
    setInputError(null);
  }, [override, period.year, period.month]);

  function goToPeriod(months: number) {
    const next = shiftPeriod(period, months);
    navigate(
      sitesHref(
        route.view,
        setFilter(route.search, FILTER_PERIOD, formatPeriodKey(next)),
        route.rowId,
      ),
      { replace: true },
    );
  }

  function save() {
    if (!canEdit) {
      setGateNote(
        'Ручной норматив ставит тот, кто ведёт табель. У вашей учётной записи такого права нет: попросите администратора выдать право на правку табеля.',
      );
      return;
    }
    setGateNote(null);
    // Запятую человек ставит по привычке из Excel — принимаем, но на сервер уходит точка.
    const raw = draft.trim().replace(',', '.');
    if (raw === '') {
      setInputError('Введите число часов или снимите ручной норматив кнопкой ниже.');
      return;
    }
    let hours: DecimalString;
    try {
      hours = decimalString(raw);
    } catch {
      setInputError('Часы пишутся числом: 558 или 63.5. Буквы и знаки сюда не подходят.');
      return;
    }
    setInputError(null);
    mutation.mutate({ year: period.year, month: period.month, body: { hours } });
  }

  function clearOverride() {
    if (!canEdit) {
      setGateNote(
        'Снять ручной норматив может тот, кто ведёт табель. Попросите администратора выдать право на правку табеля.',
      );
      return;
    }
    setGateNote(null);
    setInputError(null);
    mutation.mutate({ year: period.year, month: period.month, body: { hours: null } });
  }

  return (
    <div className='flex flex-col gap-4 p-5'>
      {/* Тот же календарь, что и во всём продукте. Здесь он был шестым по счёту вариантом:
          стрелки разъезжались к краям панели, а название месяца висело между ними посередине —
          на широком экране между «назад» и «вперёд» получалось восемьсот точек пустоты. */}
      <PeriodStepper
        label='Месяц норматива'
        title={formatPeriodTitle(period)}
        onPrev={() => goToPeriod(-1)}
        onNext={() => goToPeriod(1)}
      />

      <ScreenState
        data={query.data}
        error={query.error}
        subject={`норматив объекта за ${formatPeriodTitle(period).toLowerCase()}`}
        isEmpty={() => false}
        onRetry={() => void query.refetch()}
        loading={
          <div className='flex flex-col gap-2'>
            <Skeleton className='h-16 w-full' />
            <Skeleton className='h-16 w-full' />
          </div>
        }
        empty={null}
      >
        {(norm) => {
          const differs = norm.override !== null && !sameHours(norm.override, norm.computed);
          const delta = norm.override === null ? null : diffHours(norm.override, norm.computed);
          return (
            <div className='flex flex-col gap-4'>
              <FieldList>
                <FieldRow
                  label='Расчётный норматив'
                  value={`${formatHours(norm.computed)} ${hoursWord(norm.computed)}`}
                  note={`сумма часов в сутки по действующим постам × ${countLabel(norm.days, DAY_FORMS)} месяца`}
                />
                <FieldRow
                  label='Ручной норматив'
                  value={
                    norm.override === null
                      ? null
                      : `${formatHours(norm.override)} ${hoursWord(norm.override)}`
                  }
                  missing='не задан — значит действует расчётный'
                />
                <FieldRow
                  label='Действующий норматив'
                  value={`${formatHours(norm.effective)} ${hoursWord(norm.effective)}`}
                  note={
                    norm.override === null
                      ? 'берётся расчётный: ручного значения на этот месяц нет'
                      : 'берётся ручной: он поставлен поверх расчётного'
                  }
                />
              </FieldList>

              {differs && delta !== null && (
                <Alert.Root variant='light' status='information' size='small' role='status'>
                  <Alert.Icon as={RiScales3Line} />
                  <div className='flex min-w-0 flex-col gap-0.5'>
                    <span>
                      Расхождение: ручной норматив {formatHours(norm.override)}{' '}
                      {hoursWord(norm.override ?? '0')}, по постам и дням месяца выходит{' '}
                      {formatHours(norm.computed)} {hoursWord(norm.computed)}. Разница{' '}
                      {formatHours(absHours(delta))} {hoursWord(absHours(delta))}
                      {delta.startsWith('-') ? ' в меньшую сторону' : ' в большую сторону'}.
                    </span>
                    <span className='text-paragraph-xs opacity-80'>
                      Это протокол сверки, а не ошибка: чаще всего норму считали по другому числу
                      дней. Действует ручное значение, решение остаётся за человеком.
                    </span>
                  </div>
                </Alert.Root>
              )}

              <div className='flex flex-col gap-2 rounded-xl bg-bg-white-0 p-4 ring-1 ring-inset ring-stroke-soft-200'>
                <Label.Root htmlFor='norm-override'>
                  Ручной норматив на {formatPeriodTitle(period).toLowerCase()}
                </Label.Root>
                <Input.Root size='small' hasError={inputError !== null}>
                  <Input.Wrapper>
                    <Input.Input
                      id='norm-override'
                      inputMode='decimal'
                      // Через общий формат часов: сырое значение сервера — «744.0», и это
                      // была единственная точка в дробной части на весь продукт.
                      placeholder={`например ${formatHours(norm.computed)}`}
                      value={draft}
                      onChange={(event) => setDraft(event.target.value)}
                      aria-invalid={inputError !== null}
                    />
                  </Input.Wrapper>
                </Input.Root>
                {inputError !== null && (
                  <Hint.Root hasError>
                    <Hint.Icon as={RiErrorWarningLine} />
                    {inputError}
                  </Hint.Root>
                )}
                <div className='flex flex-wrap items-center gap-2'>
                  <Button.Root
                    variant='primary'
                    mode='filled'
                    size='xsmall'
                    onClick={save}
                    type='button'
                  >
                    {mutation.isPending ? 'Сохраняем…' : 'Сохранить ручной норматив'}
                  </Button.Root>
                  {norm.override !== null && (
                    <Button.Root
                      variant='neutral'
                      mode='stroke'
                      size='xsmall'
                      onClick={clearOverride}
                      type='button'
                    >
                      Снять ручной норматив
                    </Button.Root>
                  )}
                </div>
                {gateNote !== null && (
                  <Hint.Root hasError>
                    <Hint.Icon as={RiErrorWarningLine} />
                    {gateNote}
                  </Hint.Root>
                )}
                {mutation.isError && (
                  <Alert.Root variant='light' status='error' size='small' role='alert'>
                    <Alert.Icon as={RiErrorWarningLine} />
                    <div className='flex min-w-0 flex-col gap-0.5'>
                      <span>{mutation.error.envelope.message}</span>
                      {mutation.error.envelope.next_action !== undefined && (
                        <span className='text-paragraph-xs opacity-80'>
                          {mutation.error.envelope.next_action}
                        </span>
                      )}
                    </div>
                  </Alert.Root>
                )}
              </div>
            </div>
          );
        }}
      </ScreenState>
    </div>
  );
}

/** Модуль разности: знак несёт отдельная фраза, «−18 часов в меньшую сторону» звучит дважды. */
function absHours(value: string): string {
  return value.startsWith('-') ? value.slice(1) : value;
}
